import {useCallback, useEffect, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import type {
  BadgeCounts,
  DiagnosticItem,
  FilterOption,
  SkillGuide,
  SkillItem,
  SkillSubjectGroup
} from '../types';
import {DEFAULT_BADGE_COUNTS, FILTER_OPTION} from '../types';
import getLazyQueryData from 'apollo/getLazyQueryData';
import getQueryVariables from '../getQueryVariables';
import type {SkillPathSkillFieldsFragment} from '../queries/StudentSkillPath.queries.generated';
import {
  useAdaptiveAchievementLazyQuery,
  useAllSkillsLazyQuery,
  useToDoSkillsLazyQuery
} from '../queries/StudentSkillPath.queries.generated';
import type {StudentDiagnosticListFieldsFragment} from '../queries/StudentDiagnostics.queries.generated';
import {useStudentDiagnosticsListLazyQuery} from '../queries/StudentDiagnostics.queries.generated';
import useClassrooms from '../../Sidebar/hooks/useClassrooms';

const toDiagnosticItem = (sd: StudentDiagnosticListFieldsFragment): DiagnosticItem => ({
  diagnosticId: sd.diagnosticId,
  name: sd.diagnostic.name,
  subjectName: sd.diagnostic.guide.subject?.name ?? '',
  guideName: sd.diagnostic.guide.name,
  dueDate: sd.diagnosticSettings.dueDate ?? null
});

// Match student-assignment ordering: nearest due date first, no-due-date last, tiebreak by name.
const dueDateMs = (d: string | null) => (d ? new Date(d).getTime() : Infinity);
const byDueDateThenName = (a: DiagnosticItem, b: DiagnosticItem): number =>
  dueDateMs(a.dueDate) - dueDateMs(b.dueDate) || a.name.localeCompare(b.name);

/**
 * A diagnostic the server returned no verdict for stays listed: DiagnosticPracticeView gates entry
 * itself, so hiding work here would only make it unreachable.
 */
export const toOpenDiagnosticItems = (
  studentDiagnostics: StudentDiagnosticListFieldsFragment[]
): DiagnosticItem[] =>
  studentDiagnostics
    .filter((sd) => sd.isOpen !== false)
    .map(toDiagnosticItem)
    .sort(byDueDateThenName);

interface SkillPathResult {
  subjectGroups: SkillSubjectGroup[];
  diagnostics: DiagnosticItem[];
}

const skillsCache = new Map<string, SkillPathResult>();

// Badges are shared across filters within the same classroom
const badgesCache = new Map<string, BadgeCounts>();

// Call this when leaving the dashboard entirely to force a fresh fetch on return.
export function clearSkillPathCache(): void {
  skillsCache.clear();
  badgesCache.clear();
}

function skillsCacheKey(classroomId: string | undefined, filter: FilterOption): string {
  return `${classroomId ?? ''}:${filter}`;
}

function badgesCacheKey(classroomId: string | undefined): string {
  return classroomId ?? '';
}

interface RawSubject {
  id: string;
  name: string;
  urlSlug: string;
  guides: Array<{
    id: string;
    name: string;
    skills: SkillPathSkillFieldsFragment[];
  }>;
}

const TO_DO_SKILLS_SHOWN_PER_GUIDE = 5;

const toSkillItem = (gl: SkillPathSkillFieldsFragment): SkillItem => ({
  id: gl.id,
  name: gl.name,
  code: gl.code ?? null,
  myProficiencyLevel: gl.myProficiencyLevel ?? null,
  dueDate: gl.nearestSkillPathDueDate ?? null
});

function mapSubjectsToSkillGroups(
  subjects: RawSubject[],
  shownPerGuide = Infinity
): SkillSubjectGroup[] {
  return subjects.map((subject) => {
    const skillGuides: SkillGuide[] = subject.guides
      .filter((guide) => guide.skills.length > 0)
      .map((guide) => ({
        guideId: guide.id,
        guideName: guide.name,
        items: guide.skills.slice(0, shownPerGuide).map(toSkillItem),
        hiddenItems: guide.skills.slice(shownPerGuide).map(toSkillItem)
      }));
    return {
      subjectId: subject.id,
      subjectName: subject.name,
      browseUrl: `/adaptive-subjects/${subject.urlSlug}`,
      skillGuides
    };
  });
}

function parseBadgeCounts(json: string): BadgeCounts {
  try {
    const parsed = JSON.parse(json) as Record<string, number>;
    return {
      1: parsed['1'] ?? 0,
      2: parsed['2'] ?? 0,
      3: parsed['3'] ?? 0
    };
  } catch {
    return DEFAULT_BADGE_COUNTS;
  }
}

interface UseStudentSkillPathResult {
  skillSubjectGroups: SkillSubjectGroup[];
  diagnostics: DiagnosticItem[];
  badgeCounts: BadgeCounts;
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

const EMPTY_RESULT: SkillPathResult = {subjectGroups: [], diagnostics: []};

export function useStudentSkillPath(
  classroomId: string | undefined,
  filter: FilterOption
): UseStudentSkillPathResult {
  const sKey = skillsCacheKey(classroomId, filter);
  const bKey = badgesCacheKey(classroomId);

  const [activeResult, setActiveResult] = useState<SkillPathResult | null>(
    () => skillsCache.get(sKey) ?? null
  );
  const [activeBadgeCounts, setActiveBadgeCounts] = useState<BadgeCounts>(
    () => badgesCache.get(bKey) ?? DEFAULT_BADGE_COUNTS
  );

  const [isError, setIsError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);

  const [prevSKey, setPrevSKey] = useState(() => sKey);
  const [prevBKey, setPrevBKey] = useState(() => bKey);

  if (sKey !== prevSKey) {
    setPrevSKey(sKey);
    setActiveResult(skillsCache.get(sKey) ?? null);
    setIsError(false);
  }

  if (bKey !== prevBKey) {
    setPrevBKey(bKey);
    setActiveBadgeCounts(badgesCache.get(bKey) ?? DEFAULT_BADGE_COUNTS);
  }

  const {classrooms} = useClassrooms();
  // Depend on the ids, not the List identity: classroom refetches produce a new List with
  // identical contents, which would otherwise cancel and restart in-flight skill loads.
  const classroomIdsKey = classrooms.map((classroom) => classroom.getId()).join(',');

  const [fetchToDoSkills] = useToDoSkillsLazyQuery();
  const [fetchAllSkills] = useAllSkillsLazyQuery();
  const [fetchAdaptiveAchievement] = useAdaptiveAchievementLazyQuery();
  // Apollo discards a fetchPolicy passed to execute(), so getQueryVariables' never applies.
  const [fetchStudentDiagnostics] = useStudentDiagnosticsListLazyQuery({
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    let cancelled = false;

    const skillsNeeded = !skillsCache.has(sKey);
    const badgesNeeded = !badgesCache.has(bKey);

    if (!skillsNeeded && !badgesNeeded) return;

    const queryVariables = getQueryVariables(
      classroomId,
      classroomIdsKey === '' ? [] : classroomIdsKey.split(',')
    );

    const fetchSubjects = (): Promise<RawSubject[]> =>
      filter === FILTER_OPTION.TO_DO
        ? fetchToDoSkills(queryVariables).then((r) =>
            (getLazyQueryData(r).subjects?.results ?? []).map((s) => ({
              id: s.id,
              name: s.name,
              urlSlug: s.urlSlug,
              guides: s.guides.map((g) => ({id: g.id, name: g.name, skills: g.toDoSkills}))
            }))
          )
        : fetchAllSkills(queryVariables).then((r) =>
            (getLazyQueryData(r).subjects?.results ?? []).map((s) => ({
              id: s.id,
              name: s.name,
              urlSlug: s.urlSlug,
              guides: s.guides.map((g) => ({id: g.id, name: g.name, skills: g.allSkills}))
            }))
          );

    const fetchDiagnostics = (): Promise<StudentDiagnosticListFieldsFragment[]> =>
      fetchStudentDiagnostics(queryVariables).then(
        (r) => getLazyQueryData(r).studentDiagnostics?.results ?? []
      );

    const loadSkills = skillsNeeded
      ? Promise.all([fetchSubjects(), fetchDiagnostics()]).then(([subjects, diagnostics]) => {
          if (cancelled) return;
          const result: SkillPathResult = {
            subjectGroups: mapSubjectsToSkillGroups(
              subjects,
              filter === FILTER_OPTION.TO_DO ? TO_DO_SKILLS_SHOWN_PER_GUIDE : undefined
            ),
            diagnostics: toOpenDiagnosticItems(diagnostics)
          };
          skillsCache.set(sKey, result);
          setActiveResult(result);
        })
      : Promise.resolve();

    const loadBadges = badgesNeeded
      ? fetchAdaptiveAchievement(queryVariables).then((r) => {
          if (cancelled) return;
          const badgeCounts = parseBadgeCounts(getLazyQueryData(r).adaptiveAchievement ?? '{}');
          badgesCache.set(bKey, badgeCounts);
          setActiveBadgeCounts(badgeCounts);
        })
      : Promise.resolve();

    // Settle independently: the decorative badge counts must not block or discard a
    // successfully loaded skill path (and a skill-path failure must not blank the badges).
    void Promise.allSettled([loadSkills, loadBadges]).then(([skillsOutcome, badgesOutcome]) => {
      if (cancelled) return;
      // Derive from each settle so a later successful refetch (e.g. once classrooms populate)
      // clears a prior error instead of pinning the retry panel over loaded content.
      // Skills failure shows the inline retry panel, so it doesn't also toast.
      setIsError(skillsOutcome.status === 'rejected');
      if (skillsOutcome.status === 'fulfilled' && badgesOutcome.status === 'rejected') {
        // Skills rendered but the decorative badges failed — the toast is the only signal.
        addToast({
          color: 'negative',
          message: 'Failed to load skill path.'
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    sKey,
    bKey,
    classroomId,
    classroomIdsKey,
    filter,
    fetchToDoSkills,
    fetchAllSkills,
    fetchAdaptiveAchievement,
    fetchStudentDiagnostics,
    retryNonce
  ]);

  const retry = useCallback(() => {
    // The failure was never cached, so re-running the effect refetches only what failed.
    setIsError(false);
    setRetryNonce((nonce) => nonce + 1);
  }, []);

  const {subjectGroups, diagnostics} = activeResult ?? EMPTY_RESULT;
  return {
    skillSubjectGroups: subjectGroups,
    diagnostics,
    badgeCounts: activeBadgeCounts,
    isLoading: activeResult === null && !isError,
    isError,
    retry
  };
}
